import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function fmt(iso) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * Phase 21 — small bell in the top bar. Shows the count of pending
 * reminders due within the next 24h. Click to peek at the top 5.
 */
export default function ReminderBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/reminders", {
        params: { status: "pending", horizon: "24h", limit: 5 },
      });
      setItems(data.items || []);
    } catch (e) {
      // Silent — the bell is best-effort.
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const count = items.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:bg-accent"
          data-testid="reminder-bell"
        >
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white"
              data-testid="reminder-bell-count"
            >
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="reminder-bell-panel">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Next 24h</span>
          <Badge variant="secondary">{count}</Badge>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {items.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              You're clear — nothing pending.
            </div>
          )}
          {items.map((r) => (
            <div key={r.id} className="border-b border-border/50 px-3 py-2 last:border-b-0" data-testid={`bell-item-${r.id}`}>
              <div className="text-sm font-medium text-primary">{r.title}</div>
              <div className="text-xs text-muted-foreground">{fmt(r.fire_at)}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-border p-2">
          <Button asChild size="sm" variant="ghost" className="w-full" onClick={() => setOpen(false)} data-testid="reminder-bell-inbox-link">
            <Link to="/app/reminders">Open inbox →</Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
